import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAuth } from '../../hooks/useAuth';
import { NotesTab, useSidePanel } from '../../contexts/SidePanelContext';
import { notesService } from '../../services/notesService';
import { PatientNote } from '../../models/patientNote';
import ConfirmModal from '../ConfirmModal';
import NoteCard from './NoteCard';
import NoteComposer from './NoteComposer';
import { NoteDocIcon } from '../../assets/icons';

const WIDTH_KEY = 'clinic-notes-width';
const DEFAULT_WIDTH = 360;
const MIN_WIDTH = 300;
const MAX_WIDTH = 720;
const NOTES_PER_PAGE = 20;
// Distance from the top (px) at which scrolling up triggers loading older pages.
const LOAD_MORE_THRESHOLD = 80;

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

const PatientNotesPanel: React.FC = () => {
  const sidePanel = useSidePanel();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const open = sidePanel?.activePanel === 'notes';
  const patient = sidePanel?.notesPatient ?? null;

  const clampWidth = (w: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

  const [width, setWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return stored ? clampWidth(stored) : DEFAULT_WIDTH;
  });
  const [dragging, setDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  // Same drag-resize pattern as ClinicAgentPanel (right-docked: width grows as
  // the pointer moves left).
  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);

    const onMove = (ev: MouseEvent) => {
      setWidth(clampWidth(window.innerWidth - ev.clientX));
    };
    const onUp = () => {
      setDragging(false);
      localStorage.setItem(WIDTH_KEY, String(widthRef.current));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // Notes data for the current patient. One list per tab: "Todas" is the
  // unfiltered newest-first feed; "Fijadas" queries the backend with
  // pinned=true so every pinned note is reachable, not just the loaded pages.
  const [tab, setTab] = useState<NotesTab>('all');
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<PatientNote | null>(null);
  // Bumped whenever the patient or tab changes so a slow response can't bleed
  // into the current list.
  const fetchSeqRef = useRef(0);

  // Chat-style scrolling: newest note sits at the bottom; scrolling up loads
  // older pages. Same anchoring approach as the agent's MessageList.
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  // scrollHeight captured just before an older page is requested; non-null marks
  // a pending prepend whose scroll position must be restored once it lands.
  const pendingPrependHeightRef = useRef<number | null>(null);
  const justPrependedRef = useRef(false);
  // Whether we've already jumped to the bottom for the current list.
  const didInitialScrollRef = useRef(false);

  const patientId = patient?.id;

  // A new patient always starts on "Todas". Declared before the fetch effect
  // so the (guarded) refetch with the right tab wins.
  useEffect(() => {
    setTab('all');
  }, [patientId]);

  // A launch surface may ask for a specific tab (e.g. the pinned-notes strip
  // opens "Fijadas"). Declared after the patient reset so the request wins.
  const notesTabRequest = sidePanel?.notesTabRequest ?? null;
  useEffect(() => {
    if (notesTabRequest) setTab(notesTabRequest.tab);
  }, [notesTabRequest]);

  const pinnedFilter = tab === 'pinned' ? true : undefined;

  useEffect(() => {
    setNotes([]);
    setPage(1);
    setTotalPages(1);
    setError('');
    setDeleteTarget(null);
    // Fresh list → re-anchor at the bottom once it loads.
    didInitialScrollRef.current = false;
    pendingPrependHeightRef.current = null;
    justPrependedRef.current = false;
    prevCountRef.current = 0;
    if (patientId == null || !open) return;

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    notesService
      .getNotes(patientId, { pinned: pinnedFilter, page: 1, perpage: NOTES_PER_PAGE })
      .then((res) => {
        if (fetchSeqRef.current !== seq) return;
        setNotes(res.results);
        setPage(res.actualPage);
        setTotalPages(res.totalPages);
      })
      .catch(() => {
        if (fetchSeqRef.current !== seq) return;
        setError('No se pudieron cargar las notas.');
      })
      .finally(() => {
        if (fetchSeqRef.current === seq) setLoading(false);
      });
  }, [patientId, open, pinnedFilter]);

  const loadMore = useCallback(async () => {
    if (patientId == null || page >= totalPages) return;
    const seq = fetchSeqRef.current;
    setLoadingMore(true);
    try {
      const next = await notesService.getNotes(patientId, {
        pinned: pinnedFilter,
        page: page + 1,
        perpage: NOTES_PER_PAGE,
      });
      if (fetchSeqRef.current !== seq) return;
      // Storage is newest-first, so older pages append at the end (they render
      // above, since the list displays reversed).
      setNotes((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...next.results.filter((n) => !seen.has(n.id))];
      });
      setPage(next.actualPage);
      setTotalPages(next.totalPages);
    } catch {
      if (fetchSeqRef.current === seq) {
        setError('No se pudieron cargar más notas.');
        pendingPrependHeightRef.current = null; // release the scroll guard
      }
    } finally {
      if (fetchSeqRef.current === seq) setLoadingMore(false);
    }
  }, [patientId, page, totalPages, pinnedFilter]);

  // Load older notes when the clinician scrolls near the top. The captured
  // height ref doubles as an in-flight guard so rapid scroll events fire once.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (
      el.scrollTop <= LOAD_MORE_THRESHOLD &&
      page < totalPages &&
      !loading &&
      !loadingMore &&
      pendingPrependHeightRef.current == null
    ) {
      pendingPrependHeightRef.current = el.scrollHeight;
      loadMore();
    }
  }, [page, totalPages, loading, loadingMore, loadMore]);

  // After older notes prepend (visually, at the top), keep the viewport
  // anchored on the note the clinician was looking at. Runs before paint.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && pendingPrependHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - pendingPrependHeightRef.current;
      pendingPrependHeightRef.current = null;
      justPrependedRef.current = true;
    }
  }, [notes]);

  // Short lists can't scroll, so the scroll-up trigger would never fire even
  // with older pages available. Keep loading until the list overflows.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore) return;
    if (notes.length > 0 && page < totalPages && el.scrollHeight <= el.clientHeight) {
      loadMore();
    }
  }, [notes, loading, loadingMore, page, totalPages, loadMore]);

  // Anchor at the bottom (newest note) when the list first loads, and follow
  // new notes as they're created. Prepends must not yank the view down.
  useLayoutEffect(() => {
    const grew = notes.length > prevCountRef.current;
    prevCountRef.current = notes.length;
    if (justPrependedRef.current) {
      justPrependedRef.current = false;
      return;
    }
    const el = scrollRef.current;
    if (!el || notes.length === 0) return;

    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      el.scrollTop = el.scrollHeight;
      requestAnimationFrame(() => {
        const e = scrollRef.current;
        if (e) e.scrollTop = e.scrollHeight;
      });
      return;
    }
    if (grew) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [notes]);

  const markSaving = (id: number, saving: boolean) =>
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(id);
      else next.delete(id);
      return next;
    });

  const mutationError = (err: any) =>
    setError(
      err?.status === 403
        ? 'No tienes permiso para modificar esta nota.'
        : 'No se pudo completar la acción.',
    );

  const createNote = async (text: string) => {
    if (patientId == null) return;
    const created = await notesService.createNote(patientId, { text });
    // New notes are unpinned: prepend on "Todas"; from "Fijadas" jump to
    // "Todas" (the tab switch refetches and the new note comes with it).
    if (tab === 'all') setNotes((prev) => [created, ...prev]);
    else setTab('all');
    setError('');
  };

  const togglePin = async (note: PatientNote) => {
    if (patientId == null) return;
    markSaving(note.id, true);
    try {
      const updated = await notesService.updateNote(patientId, note.id, {
        isPinned: !note.isPinned,
      });
      if (tab === 'pinned' && !updated.isPinned) {
        // No longer belongs to the pinned filter.
        setNotes((prev) => prev.filter((n) => n.id !== note.id));
      } else {
        setNotes((prev) => prev.map((n) => (n.id === note.id ? updated : n)));
      }
      setError('');
    } catch (err) {
      mutationError(err);
    } finally {
      markSaving(note.id, false);
    }
  };

  const saveEdit = async (note: PatientNote, text: string) => {
    if (patientId == null) return;
    markSaving(note.id, true);
    try {
      const updated = await notesService.updateNote(patientId, note.id, { text });
      setNotes((prev) => prev.map((n) => (n.id === note.id ? updated : n)));
      setError('');
    } catch (err) {
      mutationError(err);
      throw err; // keep the card in edit mode
    } finally {
      markSaving(note.id, false);
    }
  };

  const confirmDelete = async () => {
    const note = deleteTarget;
    if (!note || patientId == null) return;
    setDeleteTarget(null);
    markSaving(note.id, true);
    try {
      await notesService.deleteNote(patientId, note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      setError('');
    } catch (err) {
      mutationError(err);
    } finally {
      markSaving(note.id, false);
    }
  };

  const canManage = (note: PatientNote): boolean =>
    !!user &&
    (user.role === 'admin' ||
      (note.author != null && Number(note.author.id) === Number(user.id)));

  // Closed → nothing at all: the panel is only opened from a patient's notes
  // button (list row or profile header). No floating launcher.
  if (!sidePanel || !open) return null;

  const containerStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: '70vh',
        zIndex: 1000,
        background: '#fff',
        borderTop: '1px solid #e5e7eb',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
        width,
        flexShrink: 0,
        background: '#fff',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
      };

  const renderNote = (note: PatientNote) => (
    <NoteCard
      key={note.id}
      note={note}
      canManage={canManage(note)}
      saving={savingIds.has(note.id)}
      onTogglePin={() => togglePin(note)}
      onSaveEdit={(text) => saveEdit(note, text)}
      onDelete={() => setDeleteTarget(note)}
    />
  );

  return (
    <aside style={containerStyle}>
      {/* Resize handle (desktop only) */}
      {!isMobile && (
        <div
          onMouseDown={onResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ajustar ancho del panel"
          style={{
            position: 'absolute',
            left: -3,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1,
            background: dragging ? 'rgba(56,178,172,0.35)' : 'transparent',
          }}
        />
      )}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 14px',
          borderBottom: '1px solid #e5e7eb',
          background: '#fff',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <NoteDocIcon size={18} color="#0f766e" />
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>Notas</span>
        </span>
        <button
          onClick={() => sidePanel.closeNotes()}
          aria-label="Cerrar notas"
          style={{
            border: 'none',
            background: 'transparent',
            color: '#6b7280',
            fontSize: 20,
            cursor: 'pointer',
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      </div>

      {/* Patient band — whose notes these are must be unmistakable */}
      <div
        style={{
          padding: '10px 14px',
          background: '#f0fdfa',
          borderBottom: '1px solid #ccfbf1',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {patient ? (
          <>
            <span
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                background: '#ccfbf1',
                color: '#0f766e',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {initials(patient.name)}
            </span>
            <span style={{ minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color: '#0f766e',
                }}
              >
                Notas de:
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#134e4a',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {patient.name}
              </span>
            </span>
          </>
        ) : (
          <span style={{ fontSize: 13, color: '#0f766e' }}>
            Abre las notas desde un paciente.
          </span>
        )}
      </div>

      {/* Tabs: full feed vs pinned-only (backend-filtered) */}
      {patient && (
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb',
            background: '#fff',
            padding: '0 12px',
            gap: 4,
          }}
        >
          {([
            { key: 'all', label: 'Todas' },
            { key: 'pinned', label: 'Fijadas' },
          ] as { key: NotesTab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: '10px 10px 8px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                color: tab === key ? '#0f766e' : '#6b7280',
                borderBottom: tab === key ? '2px solid #0f766e' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Notes list — newest at the bottom; scrolling up loads older pages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          background: '#f9fafb',
        }}
      >
        {error && (
          <div
            style={{
              padding: '8px 12px',
              background: '#fef2f2',
              color: '#b91c1c',
              fontSize: 12,
              borderRadius: 8,
              border: '1px solid #fecaca',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 24 }}>
            Cargando notas…
          </div>
        ) : !patient ? null : notes.length === 0 && !error ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 24 }}>
            {tab === 'pinned'
              ? 'Este paciente no tiene notas fijadas.'
              : 'Este paciente no tiene notas todavía.'}
          </div>
        ) : (
          <>
            {loadingMore && (
              <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: 4 }}>
                Cargando notas anteriores…
              </div>
            )}
            {/* Storage is newest-first; render reversed so the most recent
                note sits at the bottom, chat-style. */}
            {[...notes].reverse().map(renderNote)}
          </>
        )}
      </div>

      <NoteComposer disabled={!patient || loading} onSubmit={createNote} />

      <ConfirmModal
        open={!!deleteTarget}
        title="Eliminar nota"
        message="¿Seguro que quieres eliminar esta nota? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmButtonColor="#dc2626"
        confirmButtonHoverColor="#b91c1c"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </aside>
  );
};

export default PatientNotesPanel;
